import { useState, useEffect, useContext } from 'react';
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { Navigate, Outlet, Route, Routes, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import { AppContext } from './contexts/app.context';
import { AdminProvider, AdminContext } from './contexts/admin.context';
import {
  Dashboard,
  Home,
  Login,
  Magic,
  OtherGroupsRegistrationForm,
  SchoolRegistrationForm,
} from './pages';
import { BaseContainer, Loader } from './components';
import { adminApi, grandEstApi, fetchTranslations, getDashboardDetails } from './service';
import {
  AdminDashboard,
  AdminLogin,
  AdminMagic,
  AdminRegistrationDetails,
  AdminRegistrations,
} from './pages/admin';

function App() {
  const [translationsLoaded, setTranslationsLoaded] = useState(false);

  const loadTranslations = async () => {
    let lang = 'fr';

    i18next.init({
      interpolation: { escapeValue: false },
      saveMissing: false,
      returnEmptyString: false,
    });
    i18next.languages = ['en', 'fr', 'de', 'it'];
    i18next.use(initReactI18next).init({
      fallbackLng: 'fr',
      lng: lang,
      ns: ['translations'],
      defaultNS: 'translations',
    });

    const { data: { en, de, fr, it } = {} } = await fetchTranslations();

    i18next.addResourceBundle('en', 'translations', en);
    i18next.addResourceBundle('fr', 'translations', fr);
    i18next.addResourceBundle('de', 'translations', de);
    i18next.addResourceBundle('it', 'translations', it);

    if (window.location.pathname.includes('/admin')) {
      i18next.changeLanguage('fr');
    } else i18next.changeLanguage(lang); // Initial language

    setTranslationsLoaded(true);
  };

  useEffect(() => {
    if (translationsLoaded) return;

    loadTranslations();
  }, [translationsLoaded]);

  if (!translationsLoaded)
    return (
      <Loader
        backgroundClassName={window.location.pathname.includes('/admin') ? 'bg-teal-10/80' : ''}
      />
    );

  return (
    <>
      <Routes>
        <Route path="/" element={<BaseContainer />}>
          <Route path="" element={<Home />} />
          <Route path="login" element={<Login />} />
          <Route path="form" element={<Outlet />}>
            <Route path="" element={<SchoolRegistrationForm />} />
            <Route path="schools" element={<SchoolRegistrationForm />} />
            <Route path="other-groups" element={<OtherGroupsRegistrationForm />} />
          </Route>
          <Route path="magic" element={<Magic />} />
          <Route path="dashboard" element={<DashboardWrapper />}>
            <Route path="" element={<Dashboard />} />
          </Route>
        </Route>

        <Route path="/admin" element={<Outlet />}>
          <Route index element={<Navigate to="/admin/login" />} />
          <Route path="login" element={<AdminLogin />} />
          <Route path="magic" element={<AdminMagic />} />
          <Route
            path="dashboard"
            element={
              <AdminProvider>
                <AdminDashboardWrapper />
              </AdminProvider>
            }>
            <Route path="" element={<AdminDashboard />} />
            <Route path="registrations" element={<AdminRegistrations />} />
            <Route path="registrations/:id" element={<AdminRegistrationDetails />} />
          </Route>
          <Route path="*" element={<Navigate to="/admin/dashboard" />} />
        </Route>

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      <ToastContainer hideProgressBar pauseOnHover />
    </>
  );
}

export default App;

const DashboardWrapper = () => {
  const navigate = useNavigate();

  const { setProfile } = useContext(AppContext);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        let apiToken = grandEstApi.defaults.headers.common['Authorization'];

        if (!apiToken || apiToken === 'Bearer undefined') {
          apiToken = localStorage.getItem('token');
          grandEstApi.defaults.headers.common['Authorization'] = `Bearer ${apiToken}`;
        }

        if (!apiToken) {
          navigate('/login');
          return;
        }

        try {
          const { data } = await getDashboardDetails();
          setLoading(false);
          setProfile({ ...data, loggedIn: true });
        } catch (error) {
          navigate('/login');
        }
      } catch (error) {
        navigate('/login');
      }
    })();
  }, []);

  if (loading) return <Loader loaderType={'sync'} bgColor={'transparent'} />;

  return <Outlet />;
};

const AdminDashboardWrapper = () => {
  const navigate = useNavigate();
  const { setAdminData } = useContext(AdminContext);
  const { setAdmin } = useContext(AppContext);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        let apiToken = adminApi.defaults.headers.common['Authorization'];

        if (!apiToken || apiToken === 'Bearer undefined') {
          apiToken = localStorage.getItem('adminToken');
          adminApi.defaults.headers.common['Authorization'] = `Bearer ${apiToken}`;
        }

        if (!apiToken) {
          navigate('/admin/login');
          return;
        }

        try {
          // Verify token by making a request
          const { data } = await adminApi.get('/me');

          // Store admin data in context
          setAdminData(data);
          setAdmin(data);
          setLoading(false);
        } catch (error) {
          // Token invalid or expired
          console.error('Admin data fetch error:', error);
          localStorage.removeItem('adminToken');
          navigate('/admin/login');
        }
      } catch (error) {
        console.error('Auth check error:', error);
        navigate('/admin/login');
      }
    })();
  }, [navigate, setAdminData]);

  if (loading) return <Loader loaderType={'sync'} backgroundClassName="bg-teal-10/80" />;

  return <Outlet />;
};
